import { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus, Pencil, Trash2, BookOpen, Save, X, Upload, Link as LinkIcon,
  ChevronDown, ExternalLink, Settings, ArrowLeft, GripVertical,
} from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";
import {
  iconForCategory,
  RESOURCE_CATEGORY_ICONS,
  RESOURCE_CATEGORY_ICON_NAMES,
} from "@/constants/resourceCategories";

/**
 * AdminResources — admin CRUD for the Resources library.
 *
 * • Categories are now backend-managed (add/rename/delete/reorder + icon pick).
 * • Left column: category list with a "Manage Categories" button that flips
 *   the right pane into a category-editor view.
 * • Right column: items in the selected category with add / edit / delete / reorder.
 */

export default function AdminResources() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState(null);
  const [form, setForm] = useState(null); // resource item form
  const [catForm, setCatForm] = useState(null); // category form
  const [managingCategories, setManagingCategories] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    const [catRes, itemsRes] = await Promise.all([
      api.get("/resource-categories"),
      api.get("/resources"),
    ]);
    setCategories(catRes.data);
    setItems(itemsRes.data);
    setActiveCat((curr) => curr ?? (catRes.data[0]?.id || null));
  };

  useEffect(() => {
    setLoading(true);
    load()
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const itemsByCat = useMemo(() => {
    const map = {};
    for (const c of categories) map[c.id] = [];
    for (const it of items) if (map[it.category_id]) map[it.category_id].push(it);
    return map;
  }, [items, categories]);

  const activeCategory = categories.find((c) => c.id === activeCat);
  const activeItems = itemsByCat[activeCat] || [];

  // ---------- Item CRUD ----------
  const openCreate = () => setForm({
    category_id: activeCat,
    title: "",
    description: "",
    url: "",
    file_id: null,
    fileName: null,
  });

  const openEdit = (it) => setForm({
    id: it.id,
    category_id: it.category_id,
    title: it.title || "",
    description: it.description || "",
    url: it.url || "",
    file_id: it.file_id || null,
    fileName: it.file_id ? "Uploaded file" : null,
    order: it.order ?? 0,
  });

  const save = async () => {
    if (!form.title?.trim()) return toast.error("Title is required");
    if (!form.file_id && !form.url?.trim()) {
      return toast.error("Upload a file or paste a URL");
    }
    const body = {
      category_id: form.category_id,
      title: form.title.trim(),
      description: form.description?.trim() || null,
      url: form.url?.trim() || null,
      file_id: form.file_id || null,
      order: form.order ?? 0,
    };
    try {
      if (form.id) {
        await api.put(`/admin/resources/${form.id}`, body);
        toast.success("Resource updated");
      } else {
        await api.post("/admin/resources", body);
        toast.success("Resource added");
      }
      setForm(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = (it) => {
    setConfirm({
      title: `Delete "${it.title}"?`,
      desc: "This removes the resource from the library. The underlying file (if uploaded) stays in storage. Cannot be undone.",
      onConfirm: async () => {
        try {
          await api.delete(`/admin/resources/${it.id}`);
          toast.success("Resource deleted");
          setConfirm(null);
          await load();
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Delete failed");
        }
      },
    });
  };

  const move = async (idx, dir) => {
    const list = [...activeItems];
    const next = idx + dir;
    if (next < 0 || next >= list.length) return;
    const [m] = list.splice(idx, 1);
    list.splice(next, 0, m);
    setItems((prev) => {
      const others = prev.filter((p) => p.category_id !== activeCat);
      return [...others, ...list];
    });
    try {
      await api.post(`/admin/resources/category/${activeCat}/reorder`, { ids: list.map((x) => x.id) });
    } catch (e) {
      toast.error("Reorder failed");
      load();
    }
  };

  // ---------- Category CRUD ----------
  const openCreateCategory = () => setCatForm({
    title: "", description: "", icon: RESOURCE_CATEGORY_ICON_NAMES[0],
  });

  const openEditCategory = (c) => setCatForm({
    id: c.id,
    title: c.title || "",
    description: c.description || "",
    icon: c.icon || RESOURCE_CATEGORY_ICON_NAMES[0],
  });

  const saveCategory = async () => {
    if (!catForm.title?.trim()) return toast.error("Title is required");
    const body = {
      title: catForm.title.trim(),
      description: catForm.description?.trim() || null,
      icon: catForm.icon || null,
    };
    try {
      if (catForm.id) {
        await api.put(`/admin/resource-categories/${catForm.id}`, body);
        toast.success("Category updated");
      } else {
        const { data } = await api.post("/admin/resource-categories", body);
        toast.success("Category added");
        setActiveCat(data.id);
      }
      setCatForm(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const deleteCategory = (c) => {
    const count = itemsByCat[c.id]?.length || 0;
    if (count > 0) {
      return toast.error(`Move or delete the ${count} resource${count === 1 ? "" : "s"} inside first`);
    }
    setConfirm({
      title: `Delete "${c.title}"?`,
      desc: "This removes the category. Cannot be undone.",
      onConfirm: async () => {
        try {
          await api.delete(`/admin/resource-categories/${c.id}`);
          toast.success("Category deleted");
          setConfirm(null);
          if (activeCat === c.id) setActiveCat(null);
          await load();
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Delete failed");
        }
      },
    });
  };

  const reorderCategories = async (from, to) => {
    if (from === to || from == null || to == null) return;
    if (to < 0 || to >= categories.length) return;
    const reordered = [...categories];
    const [m] = reordered.splice(from, 1);
    reordered.splice(to, 0, m);
    setCategories(reordered);
    try {
      await api.post("/admin/resource-categories/reorder", { ids: reordered.map((c) => c.id) });
    } catch (e) {
      toast.error("Reorder failed");
      load();
    }
  };

  const moveCategory = (idx, dir) => reorderCategories(idx, idx + dir);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto" data-testid="admin-resources-page">
        <div className="flex items-center justify-center py-20"><span className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="admin-resources-page">
      <Header />

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
        <CategoryColumn
          categories={categories}
          activeId={activeCat}
          onSelect={(id) => { setActiveCat(id); setManagingCategories(false); }}
          itemsByCat={itemsByCat}
          onManage={() => setManagingCategories(true)}
        />

        {managingCategories ? (
          <CategoryManager
            categories={categories}
            itemsByCat={itemsByCat}
            onAdd={openCreateCategory}
            onEdit={openEditCategory}
            onDelete={deleteCategory}
            onMove={moveCategory}
            onReorder={reorderCategories}
            onBack={() => setManagingCategories(false)}
          />
        ) : (
          <ItemPane
            activeCategory={activeCategory}
            activeItems={activeItems}
            onAdd={openCreate}
            onEdit={openEdit}
            onDelete={remove}
            onMove={move}
          />
        )}
      </div>

      {form && (
        <ResourceFormModal
          form={form}
          setForm={setForm}
          categories={categories}
          onSave={save}
          onClose={() => setForm(null)}
        />
      )}

      {catForm && (
        <CategoryFormModal
          form={catForm}
          setForm={setCatForm}
          onSave={saveCategory}
          onClose={() => setCatForm(null)}
        />
      )}

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        description={confirm?.desc}
        destructive
        confirmLabel="Delete"
        onCancel={() => setConfirm(null)}
        onConfirm={confirm?.onConfirm}
      />
    </div>
  );
}

function Header() {
  return (
    <div className="flex items-start gap-4 mb-7">
      <div
        className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
        style={{ background: "var(--lime-glow)", color: "var(--lime)" }}
      >
        <BookOpen size={22} strokeWidth={1.6} />
      </div>
      <div>
        <div className="section-tag mb-2"><span className="dot" /> Admin</div>
        <h1 className="h-display text-3xl sm:text-4xl">Manage Resources</h1>
        <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--text-muted)" }}>
          Add, edit, and remove categories and resources. Upload a file or paste a public URL.
        </p>
      </div>
    </div>
  );
}

function CategoryColumn({ categories, activeId, onSelect, itemsByCat, onManage }) {
  return (
    <div className="surface-card p-4">
      <div className="flex items-center justify-between mb-4 px-1">
        <span className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--text)" }}>
          Categories ({categories.length})
        </span>
        <button
          onClick={onManage}
          className="p-1.5 rounded hover:bg-white/5"
          style={{ color: "var(--text-muted)" }}
          title="Manage categories"
          data-testid="admin-categories-manage"
        >
          <Settings size={14} />
        </button>
      </div>
      <div className="flex flex-col gap-2">
        {categories.map((c) => {
          const isActive = c.id === activeId;
          const Icon = iconForCategory(c.icon);
          const count = itemsByCat[c.id]?.length || 0;
          return (
            <button
              key={c.id}
              onClick={() => onSelect(c.id)}
              className="flex items-start gap-3 p-3 rounded-2xl text-left transition"
              style={{
                background: isActive ? "var(--lime-glow)" : "var(--surface2)",
                border: isActive ? "1px solid var(--lime)" : "1px solid var(--border)",
              }}
              data-testid={`admin-cat-${c.id}`}
            >
              <Icon size={16} className="mt-0.5 shrink-0" style={{ color: isActive ? "var(--lime)" : "var(--text-muted)" }} />
              <div className="min-w-0">
                <div className="text-sm font-semibold whitespace-nowrap" style={{ color: isActive ? "var(--lime)" : "var(--text)" }}>
                  {c.title}
                </div>
                <div className="font-mono text-[10px] mt-0.5" style={{ color: "var(--text-dim)" }}>
                  {count} {count === 1 ? "item" : "items"}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ItemPane({ activeCategory, activeItems, onAdd, onEdit, onDelete, onMove }) {
  if (!activeCategory) {
    return (
      <div className="surface-card p-10 text-center">
        <h3 className="font-semibold mb-1">No category selected</h3>
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          Pick a category from the left, or add a new one from the gear icon.
        </p>
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="h-display text-2xl mb-1">{activeCategory.title}</h2>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>{activeCategory.description}</p>
        </div>
        <button
          onClick={onAdd}
          className="px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2"
          style={{ background: "var(--lime)", color: "#0c0c0c" }}
          data-testid="admin-resource-add"
        >
          <Plus size={14} /> Add Resource
        </button>
      </div>

      {activeItems.length === 0 ? (
        <div className="surface-card p-10 text-center">
          <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: "var(--surface2)", color: "var(--text-muted)" }}>
            <Plus size={18} />
          </div>
          <h3 className="font-semibold mb-1">No resources yet</h3>
          <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
            Add the first contract, script, or template to this category.
          </p>
          <button
            onClick={onAdd}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold"
            style={{ background: "var(--lime)", color: "#0c0c0c" }}
          >
            Add Resource
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {activeItems.map((it, i) => (
            <AdminResourceRow
              key={it.id}
              item={it}
              index={i}
              total={activeItems.length}
              onEdit={() => onEdit(it)}
              onDelete={() => onDelete(it)}
              onMove={(dir) => onMove(i, dir)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryManager({ categories, itemsByCat, onAdd, onEdit, onDelete, onMove, onReorder, onBack }) {
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const endDrag = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  return (
    <div data-testid="admin-category-manager">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-xs mb-2 hover:opacity-80"
            style={{ color: "var(--text-muted)" }}
            data-testid="admin-categories-back"
          >
            <ArrowLeft size={12} /> Back to resources
          </button>
          <h2 className="h-display text-2xl mb-1">Manage Categories</h2>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            Drag the handle to reorder, rename, or add new categories. Delete only works when the category is empty.
          </p>
        </div>
        <button
          onClick={onAdd}
          className="px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2"
          style={{ background: "var(--lime)", color: "#0c0c0c" }}
          data-testid="admin-category-add"
        >
          <Plus size={14} /> New Category
        </button>
      </div>

      <div className="flex flex-col gap-3">
        {categories.map((c, i) => {
          const Icon = iconForCategory(c.icon);
          const count = itemsByCat[c.id]?.length || 0;
          return (
            <div
              key={c.id}
              draggable
              onDragStart={(e) => {
                setDragIndex(i);
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", c.id);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (dragIndex !== null && i !== overIndex) setOverIndex(i);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragIndex !== null) onReorder(dragIndex, i);
                endDrag();
              }}
              onDragEnd={endDrag}
              className="p-4 rounded-2xl transition-opacity"
              style={{
                background: "var(--surface)",
                border: overIndex === i && dragIndex !== null && dragIndex !== i
                  ? "1px solid var(--lime)"
                  : "1px solid var(--border)",
                opacity: dragIndex === i ? 0.45 : 1,
                cursor: "grab",
              }}
              data-testid={`admin-category-row-${c.id}`}
            >
              <div className="flex items-start gap-3">
                <div
                  className="mt-2 shrink-0 hidden sm:block"
                  style={{ color: "var(--text-dim)", cursor: "grab" }}
                  title="Drag to reorder"
                  data-testid={`admin-category-drag-${c.id}`}
                >
                  <GripVertical size={14} />
                </div>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--surface2)", color: "var(--text-muted)" }}>
                  <Icon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center flex-wrap gap-2">
                    <h4 className="text-sm font-semibold">{c.title}</h4>
                    <span className="font-mono text-[10px] px-2 py-0.5 rounded-full" style={{ background: "var(--surface2)", color: "var(--text-muted)" }}>
                      {count} {count === 1 ? "item" : "items"}
                    </span>
                  </div>
                  {c.description && (
                    <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{c.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => onMove(i, -1)} disabled={i === 0} className="p-1.5 rounded hover:bg-white/5 disabled:opacity-40 sm:hidden" title="Move up" data-testid={`admin-category-up-${c.id}`}>
                    <ChevronDown size={12} className="rotate-180" />
                  </button>
                  <button onClick={() => onMove(i, 1)} disabled={i === categories.length - 1} className="p-1.5 rounded hover:bg-white/5 disabled:opacity-40 sm:hidden" title="Move down" data-testid={`admin-category-down-${c.id}`}>
                    <ChevronDown size={12} />
                  </button>
                  <button onClick={() => onEdit(c)} className="p-1.5 rounded hover:bg-white/5" data-testid={`admin-category-edit-${c.id}`}>
                    <Pencil size={13} />
                  </button>
                  <button
                    onClick={() => onDelete(c)}
                    disabled={count > 0}
                    className="p-1.5 rounded hover:bg-white/5 disabled:opacity-40"
                    style={{ color: "#f87171" }}
                    title={count > 0 ? `Move or delete the ${count} resource${count === 1 ? "" : "s"} inside first` : "Delete category"}
                    data-testid={`admin-category-delete-${c.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AdminResourceRow({ item, index, total, onEdit, onDelete, onMove }) {
  const href = item.file_id ? fileUrl(item.file_id) : item.url;
  return (
    <div
      className="p-4 rounded-2xl"
      style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
      data-testid={`admin-resource-row-${item.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full flex items-center justify-center font-mono text-xs shrink-0" style={{ background: "var(--surface2)", color: "var(--text-muted)" }}>
          {index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <h4 className="text-sm font-semibold">{item.title}</h4>
            <span className="font-mono text-[10px] px-2 py-0.5 rounded-full" style={{ background: item.file_id ? "rgba(34, 197, 94, 0.15)" : "rgba(96, 165, 250, 0.12)", color: item.file_id ? "#22c55e" : "#60a5fa" }}>
              {item.file_id ? "Uploaded" : "External URL"}
            </span>
          </div>
          {item.description && (
            <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{item.description}</p>
          )}
          {href && (
            <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 font-mono text-[11px]" style={{ color: "var(--lime)" }}>
              <ExternalLink size={11} /> Preview
            </a>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => onMove(-1)} disabled={index === 0} className="p-1.5 rounded hover:bg-white/5 disabled:opacity-40" title="Move up">
            <ChevronDown size={12} className="rotate-180" />
          </button>
          <button onClick={() => onMove(1)} disabled={index === total - 1} className="p-1.5 rounded hover:bg-white/5 disabled:opacity-40" title="Move down">
            <ChevronDown size={12} />
          </button>
          <button onClick={onEdit} className="p-1.5 rounded hover:bg-white/5" data-testid={`admin-resource-edit-${item.id}`}>
            <Pencil size={13} />
          </button>
          <button onClick={onDelete} className="p-1.5 rounded hover:bg-white/5" style={{ color: "#f87171" }} data-testid={`admin-resource-delete-${item.id}`}>
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

function ResourceFormModal({ form, setForm, categories, onSave, onClose }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, file_id: data.id, fileName: data.original_filename, url: "" }));
      toast.success("File uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="admin-resource-modal"
    >
      <div
        className="surface-card w-full max-w-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-3">
          <h3 className="text-lg font-semibold">{form.id ? "Edit Resource" : "Add Resource"}</h3>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/5" style={{ color: "var(--text-muted)" }}>
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pb-2 space-y-4">
          <Field label="Category">
            <select
              value={form.category_id}
              onChange={(e) => setForm({ ...form, category_id: e.target.value })}
              className="input-base"
              data-testid="admin-resource-category-select"
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          </Field>

          <Field label="Title">
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="input-base"
              placeholder="e.g. Buyer Agreement"
              autoFocus
              data-testid="admin-resource-title-input"
            />
          </Field>

          <Field label="Description" hint="One short line. Students see this on the card.">
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="input-base"
              placeholder="Short description..."
              data-testid="admin-resource-description-input"
            />
          </Field>

          <div className="rounded-2xl p-4" style={{ background: "var(--surface2)", border: "1px solid var(--border)" }}>
            <div className="font-mono text-xs uppercase tracking-wider mb-3" style={{ color: "var(--text)" }}>
              File (upload OR URL)
            </div>

            {form.file_id ? (
              <div className="flex items-center justify-between gap-3 p-3 rounded-xl" style={{ background: "rgba(34, 197, 94, 0.08)", border: "1px solid rgba(34, 197, 94, 0.3)" }}>
                <div className="text-sm font-mono truncate" style={{ color: "#22c55e" }}>
                  ✓ {form.fileName || "Uploaded file"}
                </div>
                <button
                  onClick={() => setForm({ ...form, file_id: null, fileName: null })}
                  className="text-xs hover:underline shrink-0"
                  style={{ color: "var(--text-muted)" }}
                >
                  Remove
                </button>
              </div>
            ) : (
              <>
                <label className="flex items-center justify-center gap-2 p-3 rounded-xl cursor-pointer transition hover:bg-white/5" style={{ border: "1px dashed var(--border)" }}>
                  <Upload size={14} style={{ color: "var(--lime)" }} />
                  <span className="text-sm" style={{ color: "var(--text-muted)" }}>
                    {uploading ? "Uploading..." : "Upload PDF / DOCX / Image (max 15MB)"}
                  </span>
                  <input
                    ref={fileRef}
                    type="file"
                    onChange={onPickFile}
                    accept=".pdf,.docx,.doc,.xlsx,.xls,.png,.jpg,.jpeg,.webp,.gif"
                    className="hidden"
                    data-testid="admin-resource-file-input"
                  />
                </label>

                <div className="flex items-center gap-3 my-3">
                  <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
                  <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: "var(--text-dim)" }}>or</span>
                  <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
                </div>

                <div className="relative">
                  <LinkIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
                  <input
                    type="url"
                    value={form.url}
                    onChange={(e) => setForm({ ...form, url: e.target.value, file_id: null, fileName: null })}
                    className="input-base pl-9"
                    placeholder="https://... (paste a public URL)"
                    data-testid="admin-resource-url-input"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="px-6 pb-6 pt-3 flex items-center justify-end gap-2 border-t" style={{ borderColor: "var(--border)" }}>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm" style={{ border: "1px solid var(--border)", color: "var(--text-muted)" }}>Cancel</button>
          <button
            onClick={onSave}
            disabled={uploading}
            className="px-5 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
            style={{ background: "var(--lime)", color: "#0c0c0c" }}
            data-testid="admin-resource-save"
          >
            <Save size={14} /> Save
          </button>
        </div>
      </div>
    </div>
  );
}

function CategoryFormModal({ form, setForm, onSave, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="admin-category-modal"
    >
      <div
        className="surface-card w-full max-w-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-3">
          <h3 className="text-lg font-semibold">{form.id ? "Edit Category" : "New Category"}</h3>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/5" style={{ color: "var(--text-muted)" }}>
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pb-2 space-y-4">
          <Field label="Title">
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="input-base"
              placeholder="e.g. Marketing Templates"
              autoFocus
              data-testid="admin-category-title-input"
            />
          </Field>

          <Field label="Description" hint="Shown under the category title on the student page.">
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="input-base"
              placeholder="What's inside this category…"
              data-testid="admin-category-description-input"
            />
          </Field>

          <Field label="Icon" hint="Pick one — appears next to the category name.">
            <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
              {RESOURCE_CATEGORY_ICON_NAMES.map((name) => {
                const Icon = RESOURCE_CATEGORY_ICONS[name];
                const isActive = form.icon === name;
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setForm({ ...form, icon: name })}
                    className="aspect-square rounded-xl flex items-center justify-center transition"
                    style={{
                      background: isActive ? "var(--lime-glow)" : "var(--surface2)",
                      border: isActive ? "1px solid var(--lime)" : "1px solid var(--border)",
                      color: isActive ? "var(--lime)" : "var(--text-muted)",
                    }}
                    title={name}
                    data-testid={`admin-category-icon-${name}`}
                  >
                    <Icon size={16} />
                  </button>
                );
              })}
            </div>
          </Field>
        </div>

        <div className="px-6 pb-6 pt-3 flex items-center justify-end gap-2 border-t" style={{ borderColor: "var(--border)" }}>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm" style={{ border: "1px solid var(--border)", color: "var(--text-muted)" }}>Cancel</button>
          <button
            onClick={onSave}
            className="px-5 py-2 rounded-xl text-sm font-semibold flex items-center gap-2"
            style={{ background: "var(--lime)", color: "#0c0c0c" }}
            data-testid="admin-category-save"
          >
            <Save size={14} /> Save
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-mono uppercase tracking-wider mb-1.5" style={{ color: "var(--text-muted)" }}>
        {label}
      </label>
      {children}
      {hint && <div className="text-[11px] mt-1" style={{ color: "var(--text-dim)" }}>{hint}</div>}
    </div>
  );
}
